/**
 * Admin Page Builder
 *
 * Streamlined component-based page builder. Create pages directly,
 * add blocks from the registry or define custom blocks, configure
 * each block's fields inline, toggle visibility, reorder, and preview.
 *
 * Aesthetic direction: Bi no Kisu — premium minimalism, clarity,
 * intentional spacing, strong hierarchy.
 */
import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Plus, Trash2, Eye, Save, ChevronUp, ChevronDown, ChevronRight,
  ToggleLeft, ToggleRight, Copy, Layers, Layout, FileText,
  BarChart3, Image as ImageIcon, DollarSign, HelpCircle,
  ArrowLeft, Settings, ExternalLink, Link2, Edit3,
  Puzzle, X, Upload, Loader2, Braces, Check,
  Globe, Megaphone,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import { Textarea } from '../../components/new-components/ui/textarea';
import { Switch } from '../../components/new-components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/new-components/ui/select';
import api from '../../services/api';
import { getDeals } from '../../services/adminDealFlowApi';
import {
  getAllBlocks, getBlockEntry, getBlocksByCategory, getCategories,
  PageRenderer,
} from '../../components/new-components/PageBuilder';
import type {
  BlockInstance, BlockRegistryEntry, FieldSchema, PageConfig, PageSettings, EntityContext,
} from '../../components/new-components/PageBuilder/types';
import SitePageEditor from '../../components/new-components/PageBuilder/SitePageEditor';
import { SITE_TEMPLATES } from '../../components/new-components/PageBuilder/siteTemplates';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */
interface PageRecord {
  id: string;
  template_id: string;
  template_name: string;
  title: string;
  slug: string;
  entity_type: string;
  entity_id?: string;
  section_content: PageConfig;
  status: string;
  nav_visible?: boolean;
  nav_order?: number;
  nav_type?: string;
  section_visibility?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface Deal {
  id: string;
  title: string;
  type: string;
  status: string;
}

interface PropertyItem {
  id: string;
  title: string;
}

interface TemplateRecord {
  id: string;
  name: string;
  category?: string;
  description?: string;
  template_type?: string;
  component_config?: PageConfig;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */
const unwrapList = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  return [];
};

const fmtDate = (d: string) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

let blockIdCounter = 0;
const genBlockId = () => `block-${Date.now()}-${++blockIdCounter}`;

const NONE_SENTINEL = '__none__';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  layout: <Layout size={14} />,
  content: <FileText size={14} />,
  data: <BarChart3 size={14} />,
  media: <ImageIcon size={14} />,
  investment: <DollarSign size={14} />,
  interactive: <HelpCircle size={14} />,
  marketing: <Megaphone size={14} />,
  custom: <Puzzle size={14} />,
};

const CATEGORY_LABELS: Record<string, string> = {
  layout: 'Layout',
  content: 'Content',
  data: 'Data & Metrics',
  media: 'Media',
  investment: 'Investment',
  interactive: 'Interactive',
  marketing: 'Marketing & Site',
  custom: 'Custom Blocks',
};

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'richtext', label: 'Rich Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Toggle' },
  { value: 'image', label: 'Image URL' },
  { value: 'color', label: 'Color' },
  { value: 'select', label: 'Dropdown' },
];

/* ------------------------------------------------------------------ */
/*  Image Field Editor — upload images or provide URL                  */
/* ------------------------------------------------------------------ */
function ImageFieldEditor({
  schema,
  value,
  onChange,
}: {
  schema: FieldSchema;
  value: any;
  onChange: (value: any) => void;
}) {
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('images', files[0]);
      const res = await api.post('/admin/page-templates/images/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data: any = (res as any)?.data ?? res;
      const uploaded = Array.isArray(data) ? data : data?.data;
      if (uploaded?.[0]?.url) {
        onChange(uploaded[0].url);
      }
    } catch (err) {
      console.error('Image upload failed:', err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-muted-foreground">{schema.label}</label>
      {value && (
        <div className="relative w-full h-32 rounded-lg overflow-hidden border bg-muted">
          <img src={value} alt="" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="flex-shrink-0"
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
        <Input
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Or paste image URL"
          className="text-xs"
        />
      </div>
      {schema.helpText && <p className="text-[11px] text-muted-foreground/70">{schema.helpText}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Field Editor — renders one editable field based on schema          */
/* ------------------------------------------------------------------ */
function FieldEditor({
  schema,
  value,
  onChange,
}: {
  schema: FieldSchema;
  value: any;
  onChange: (value: any) => void;
}) {
  switch (schema.type) {
    case 'image':
      return <ImageFieldEditor schema={schema} value={value} onChange={onChange} />;

    case 'text':
    case 'icon':
    case 'color':
      return (
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">{schema.label}</label>
          <Input
            value={value ?? schema.defaultValue ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={schema.placeholder || schema.label}
          />
          {schema.helpText && <p className="text-[11px] text-muted-foreground/70">{schema.helpText}</p>}
        </div>
      );

    case 'textarea':
    case 'richtext':
      return (
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">{schema.label}</label>
          <Textarea
            value={value ?? schema.defaultValue ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={schema.placeholder || schema.label}
            rows={schema.type === 'richtext' ? 6 : 3}
          />
          {schema.helpText && <p className="text-[11px] text-muted-foreground/70">{schema.helpText}</p>}
        </div>
      );

    case 'number':
      return (
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">{schema.label}</label>
          <Input
            type="number"
            value={value ?? schema.defaultValue ?? ''}
            onChange={(e) => onChange(Number(e.target.value))}
            min={schema.min}
            max={schema.max}
          />
        </div>
      );

    case 'boolean':
      return (
        <div className="flex items-center justify-between py-1">
          <label className="text-xs font-medium text-muted-foreground">{schema.label}</label>
          <Switch
            checked={value ?? schema.defaultValue ?? false}
            onCheckedChange={onChange}
          />
        </div>
      );

    case 'select':
      return (
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">{schema.label}</label>
          <Select value={String(value ?? schema.defaultValue ?? '')} onValueChange={onChange}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {schema.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'list':
      return (
        <ListFieldEditor
          schema={schema}
          value={value ?? []}
          onChange={onChange}
        />
      );

    case 'json':
      return (
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">{schema.label}</label>
          <Textarea
            value={typeof value === 'string' ? value : JSON.stringify(value ?? {}, null, 2)}
            onChange={(e) => {
              try { onChange(JSON.parse(e.target.value)); } catch { /* ignore parse errors during typing */ }
            }}
            rows={6}
            className="font-mono text-xs"
          />
        </div>
      );

    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/*  List Field Editor                                                  */
/* ------------------------------------------------------------------ */
function ListFieldEditor({
  schema,
  value,
  onChange,
}: {
  schema: FieldSchema;
  value: any[];
  onChange: (value: any[]) => void;
}) {
  const listSchema = schema.listSchema || [];
  const [jsonEditIdx, setJsonEditIdx] = useState<number | null>(null);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [copied, setCopied] = useState(false);

  const addItem = () => {
    const newItem: Record<string, any> = {};
    listSchema.forEach(f => { newItem[f.key] = f.defaultValue ?? ''; });
    onChange([...value, newItem]);
  };

  const removeItem = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const moveItem = (idx: number, dir: 'up' | 'down') => {
    const target = dir === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const updateItem = (idx: number, key: string, val: any) => {
    const next = [...value];
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };

  const openJsonForItem = (idx: number) => {
    setJsonText(JSON.stringify(value[idx], null, 2));
    setJsonError('');
    setCopied(false);
    setJsonEditIdx(idx);
  };

  const handleApplyItemJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const next = [...value];
        next[jsonEditIdx!] = parsed;
        onChange(next);
        setJsonEditIdx(null);
      } else {
        setJsonError('Expected a JSON object for the item.');
      }
    } catch (err: any) {
      setJsonError(err.message || 'Invalid JSON');
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-muted-foreground">{schema.label}</label>
      {value.map((item, idx) => (
        <div key={idx} className="p-3 rounded-lg border border-border bg-background space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">#{idx + 1}</span>
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => openJsonForItem(idx)}
                className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                title="Edit as JSON"
              >
                <Braces size={12} />
              </button>
              <button
                onClick={() => moveItem(idx, 'up')}
                disabled={idx === 0}
                className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors"
                title="Move up"
              >
                <ChevronUp size={12} />
              </button>
              <button
                onClick={() => moveItem(idx, 'down')}
                disabled={idx === value.length - 1}
                className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors"
                title="Move down"
              >
                <ChevronDown size={12} />
              </button>
              <button onClick={() => removeItem(idx)} className="p-1 text-red-500 hover:text-red-700">
                <Trash2 size={12} />
              </button>
            </div>
          </div>
          {listSchema.map(field => (
            <FieldEditor
              key={field.key}
              schema={field}
              value={item[field.key]}
              onChange={(v) => updateItem(idx, field.key, v)}
            />
          ))}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addItem} className="w-full">
        <Plus size={14} className="mr-1" /> Add {schema.label?.replace(/s$/, '') || 'Item'}
      </Button>

      {/* Per-item JSON editor dialog */}
      <Dialog open={jsonEditIdx !== null} onOpenChange={(open) => { if (!open) setJsonEditIdx(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold">
              Edit Item #{(jsonEditIdx ?? 0) + 1} as JSON
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Edit the JSON below, then click Apply. You can copy it, modify in an LLM, and paste back.
            </p>
            <div className="relative">
              <Textarea
                value={jsonText}
                onChange={e => { setJsonText(e.target.value); setJsonError(''); }}
                className="font-mono text-xs min-h-[300px] resize-y"
                spellCheck={false}
              />
            </div>
            {jsonError && <p className="text-xs text-red-500">{jsonError}</p>}
          </div>
          <DialogFooter className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(jsonText).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                });
              }}
            >
              {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
              {copied ? 'Copied!' : 'Copy JSON'}
            </Button>
            <Button size="sm" onClick={handleApplyItemJson}>Apply Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Section Card — a block rendered as a structured section            */
/*  Mirrors the Page Templates section-editing UX                      */
/* ------------------------------------------------------------------ */
function SectionCard({
  block,
  entry,
  onUpdate,
  onRemove,
  onToggle,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: {
  block: BlockInstance;
  entry: BlockRegistryEntry | null;
  onUpdate: (props: Record<string, any>) => void;
  onRemove: () => void;
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [copied, setCopied] = useState(false);
  const label = entry?.label || block.blockKey;
  const category = entry?.category || 'custom';
  const schema = entry?.schema || [];
  const filledCount = schema.filter(f => {
    const v = block.props[f.key];
    return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);
  }).length;

  const handleOpenJson = (e: React.MouseEvent) => {
    e.stopPropagation();
    setJsonText(JSON.stringify({ blockKey: block.blockKey, props: block.props }, null, 2));
    setJsonError('');
    setShowJson(true);
  };

  const handleCopyJson = () => {
    navigator.clipboard.writeText(jsonText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleApplyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (parsed && typeof parsed.props === 'object' && parsed.props !== null) {
        onUpdate(parsed.props);
        setShowJson(false);
        setJsonError('');
      } else if (parsed && !parsed.props) {
        // Allow pasting just the props object directly
        onUpdate(parsed);
        setShowJson(false);
        setJsonError('');
      } else {
        setJsonError('Expected a JSON object with a "props" key, or the props object itself.');
      }
    } catch (err: any) {
      setJsonError(err.message || 'Invalid JSON');
    }
  };

  return (
    <div className={`rounded-lg border transition-all ${
      block.visible
        ? expanded ? 'border-primary/40 shadow-sm' : 'border-border'
        : 'border-dashed border-muted-foreground/30 opacity-50'
    }`}>
      {/* Section Header */}
      <div
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer select-none ${
          expanded ? 'bg-muted/40 rounded-t-lg' : 'rounded-lg hover:bg-muted/20'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <ChevronRight size={14} className={`text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`} />
        <span className="text-muted-foreground">{CATEGORY_ICONS[category]}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{label}</p>
          <p className="text-[11px] text-muted-foreground">
            {filledCount}/{schema.length} fields configured
          </p>
        </div>
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button onClick={onMoveUp} disabled={isFirst}
            className="p-1.5 hover:bg-muted rounded disabled:opacity-20 transition-colors">
            <ChevronUp size={14} />
          </button>
          <button onClick={onMoveDown} disabled={isLast}
            className="p-1.5 hover:bg-muted rounded disabled:opacity-20 transition-colors">
            <ChevronDown size={14} />
          </button>
          <button onClick={onToggle}
            className="p-1.5 hover:bg-muted rounded transition-colors">
            {block.visible
              ? <ToggleRight size={16} className="text-green-600" />
              : <ToggleLeft size={16} className="text-muted-foreground" />
            }
          </button>
          <button
            onClick={handleOpenJson}
            className="p-1.5 hover:bg-muted rounded transition-colors text-muted-foreground hover:text-foreground"
            title="Copy / paste block JSON"
          >
            <Braces size={14} />
          </button>
          <button onClick={onRemove}
            className="p-1.5 hover:bg-red-50 rounded text-red-500 transition-colors">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Section Fields — shown inline like Page Templates */}
      {expanded && (
        <div className="border-t border-border px-4 py-4 space-y-4 bg-background rounded-b-lg">
          {schema.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">This block has no configurable fields.</p>
          ) : (
            schema.map(field => (
              <FieldEditor
                key={field.key}
                schema={field}
                value={block.props[field.key]}
                onChange={(v) => onUpdate({ ...block.props, [field.key]: v })}
              />
            ))
          )}
        </div>
      )}

      {/* JSON Editor Dialog */}
      <Dialog open={showJson} onOpenChange={setShowJson}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Braces size={16} className="text-muted-foreground" />
              Block JSON — {label}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">
            Copy this JSON into an LLM to make edits, then paste the modified result back and click Apply.
          </p>
          <div className="flex-1 overflow-hidden space-y-2 mt-2">
            <Textarea
              value={jsonText}
              onChange={(e) => { setJsonText(e.target.value); setJsonError(''); }}
              rows={20}
              className="font-mono text-xs resize-none"
              spellCheck={false}
            />
            {jsonError && (
              <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded px-3 py-2">
                {jsonError}
              </p>
            )}
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={handleCopyJson} className="gap-1.5">
              {copied
                ? <><Check size={14} className="text-green-600" /> Copied!</>
                : <><Copy size={14} /> Copy JSON</>
              }
            </Button>
            <Button variant="outline" onClick={() => setShowJson(false)}>Cancel</Button>
            <Button onClick={handleApplyJson}>Apply Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Custom Block Creator                                               */
/* ------------------------------------------------------------------ */
interface CustomFieldDef {
  key: string;
  label: string;
  type: string;
}

function CustomBlockCreator({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (entry: BlockRegistryEntry) => void;
}) {
  const [blockName, setBlockName] = useState('');
  const [blockDesc, setBlockDesc] = useState('');
  const [fields, setFields] = useState<CustomFieldDef[]>([]);

  const addField = () => {
    setFields(prev => [...prev, { key: '', label: '', type: 'text' }]);
  };

  const updateField = (idx: number, updates: Partial<CustomFieldDef>) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, ...updates } : f));
  };

  const removeField = (idx: number) => {
    setFields(prev => prev.filter((_, i) => i !== idx));
  };

  const handleCreate = () => {
    if (!blockName.trim() || fields.length === 0) return;

    const validFields = fields.filter(f => f.label.trim());
    const schema: FieldSchema[] = validFields.map(f => ({
      key: f.key || f.label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
      label: f.label,
      type: f.type as any,
    }));

    const blockKey = `custom-${blockName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`;

    const entry: BlockRegistryEntry = {
      key: blockKey,
      label: blockName.trim(),
      description: blockDesc || `Custom block: ${blockName}`,
      category: 'custom',
      icon: 'Puzzle',
      schema,
      defaultProps: Object.fromEntries(schema.map(f => [f.key, f.defaultValue ?? ''])),
    };

    onCreated(entry);
    setBlockName('');
    setBlockDesc('');
    setFields([]);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Puzzle size={18} /> Create Custom Block
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-1">
            <label className="text-xs font-medium">Block Name</label>
            <Input
              value={blockName}
              onChange={(e) => setBlockName(e.target.value)}
              placeholder="e.g. Pricing Table, Contact Form, Custom CTA"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Description</label>
            <Input
              value={blockDesc}
              onChange={(e) => setBlockDesc(e.target.value)}
              placeholder="Brief description"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-muted-foreground">
                FIELDS ({fields.length})
              </label>
              <Button variant="outline" size="sm" onClick={addField}>
                <Plus size={12} className="mr-1" /> Add Field
              </Button>
            </div>

            {fields.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4 italic">
                Add fields to define your block's editable content.
              </p>
            )}

            {fields.map((f, idx) => (
              <div key={idx} className="flex gap-2 items-start p-3 rounded-lg border border-border bg-muted/20">
                <div className="flex-1 space-y-2">
                  <Input
                    value={f.label}
                    onChange={(e) => {
                      const label = e.target.value;
                      const key = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                      updateField(idx, { label, key });
                    }}
                    placeholder="Field label"
                    className="h-8 text-sm"
                  />
                  <Select value={f.type} onValueChange={(v) => updateField(idx, { type: v })}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <button onClick={() => removeField(idx)} className="p-1.5 text-red-500 hover:text-red-700 mt-1">
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!blockName.trim() || fields.length === 0}>
            Create Block
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
const PageBuilder: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const prefill = (location.state as any)?.prefill;
  const cameFromExternal = !!prefill;
  const [view, setView] = useState<'list' | 'builder' | 'site-editor'>('list');

  // List view state
  const [pages, setPages] = useState<PageRecord[]>([]);
  const [templates, setTemplates] = useState<TemplateRecord[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [properties, setProperties] = useState<PropertyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [listTab, setListTab] = useState<'pages' | 'published' | 'templates' | 'site'>('pages');
  const [entityFilter, setEntityFilter] = useState<'all' | 'offering' | 'property' | 'asset'>('all');

  // Create page dialog
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newEntityType, setNewEntityType] = useState('offering');
  const [newEntityId, setNewEntityId] = useState('');

  // Create site page dialog
  const [showCreateSite, setShowCreateSite] = useState(false);
  const [newSiteTitle, setNewSiteTitle] = useState('');
  const [newSiteSlug, setNewSiteSlug] = useState('');
  const [newSiteTemplate, setNewSiteTemplate] = useState('landing');

  // Builder state
  const [editingPage, setEditingPage] = useState<PageRecord | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<TemplateRecord | null>(null);
  const [blocks, setBlocks] = useState<BlockInstance[]>([]);
  const [pageSettings, setPageSettings] = useState<PageSettings>({});
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showBlockPicker, setShowBlockPicker] = useState(false);
  const [showPageSettings, setShowPageSettings] = useState(false);
  const [showCustomCreator, setShowCustomCreator] = useState(false);

  // Custom block registry (persisted in page config settings)
  const [customBlocks, setCustomBlocks] = useState<BlockRegistryEntry[]>([]);

  // ── Data Loading ──
  const ensureDefaultTemplate = useCallback(async () => {
    try {
      const tplRes = await api.get('/admin/page-templates/components/list');
      const tpls = unwrapList(tplRes);
      if (tpls.length === 0) {
        await api.post('/admin/page-templates/components', {
          name: 'Default',
          category: 'general',
          description: 'Default page template',
          blocks: [],
          settings: {},
        });
      }
    } catch { /* ignore */ }
  }, []);

  const loadPages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await ensureDefaultTemplate();
      const [instRes, tplRes, dealsRes, propsRes] = await Promise.all([
        api.get('/admin/page-templates/components/instances/list'),
        api.get('/admin/page-templates/components/list'),
        getDeals({ status: 'open' }).catch(() => ({ deals: [] })),
        api.get('/admin/properties', { params: { limit: 200 } }).catch(() => ({})),
      ]);
      setPages(unwrapList(instRes));
      setTemplates(unwrapList(tplRes));
      const d = (dealsRes as any)?.deals || (dealsRes as any)?.data?.deals || [];
      setDeals(Array.isArray(d) ? d : []);
      // Extract properties for the entity picker
      const propsBody = (propsRes as any)?.data || propsRes;
      const propsArr = propsBody?.properties || propsBody?.data || [];
      setProperties((Array.isArray(propsArr) ? propsArr : []).map((p: any) => ({ id: p.id, title: p.title || p.name || 'Untitled' })));
    } catch (err: any) {
      setError(err?.message || 'Failed to load pages');
    } finally {
      setLoading(false);
    }
  }, [ensureDefaultTemplate]);

  useEffect(() => { loadPages(); }, [loadPages]);

  // ── Handle prefill from AdminAssets navigation ──
  useEffect(() => {
    if (prefill && !loading) {
      // Check if a page already exists for this entity
      const existing = pages.find(p => p.entity_type === prefill.entityType && p.entity_id === prefill.entityId);
      if (existing) {
        openBuilder(existing);
      } else {
        setNewEntityType(prefill.entityType || 'property');
        setNewEntityId(prefill.entityId || '');
        setNewTitle(prefill.title || '');
        setShowCreate(true);
      }
      // Clear the state so it doesn't re-trigger
      window.history.replaceState({}, document.title);
    }
  }, [prefill, loading, pages]);

  // ── Get default template id ──
  const getDefaultTemplateId = async (entityType: string): Promise<string | null> => {
    try {
      const tplRes = await api.get('/admin/page-templates/components/list');
      const tpls = unwrapList(tplRes);
      const lowered = (entityType || '').toLowerCase();
      if (lowered === 'offering') {
        const offeringTpl = tpls.find((t: any) => String(t?.name || '').toLowerCase() === 'fund offering');
        if (offeringTpl?.id) return offeringTpl.id;
      }
      if (lowered === 'property') {
        const propertyTpl = tpls.find((t: any) => String(t?.name || '').toLowerCase() === 'property template');
        if (propertyTpl?.id) return propertyTpl.id;
      }
      return tpls[0]?.id || null;
    } catch { return null; }
  };

  // ── Create Page ──
  const handleCreatePage = async () => {
    if (!newTitle.trim()) return;
    try {
      const templateId = await getDefaultTemplateId(newEntityType);
      if (!templateId) {
        setError('No template available. Please try again.');
        return;
      }
      const res = await api.post('/admin/page-templates/components/instances', {
        templateId,
        entityType: newEntityType,
        entityId: newEntityId && newEntityId !== NONE_SENTINEL ? newEntityId : null,
        title: newTitle.trim(),
      });
      const created = (res as any)?.data || res;
      setShowCreate(false);
      setNewTitle('');
      setNewEntityId('');
      if (created?.id) {
        openBuilder({
          ...created,
          section_content: created.section_content || { blocks: [], settings: {} },
        });
      }
      loadPages();
    } catch (err: any) {
      setError(err?.message || 'Failed to create page');
    }
  };

  // ── Create Site Page ──
  const handleCreateSitePage = async () => {
    if (!newSiteTitle.trim()) return;
    try {
      const templateId = await getDefaultTemplateId('site');
      if (!templateId) {
        setError('No template available. Please try again.');
        return;
      }
      const res = await api.post('/admin/page-templates/components/instances', {
        templateId,
        entityType: 'site',
        entityId: null,
        title: newSiteTitle.trim(),
        slug: newSiteSlug.trim() || undefined,
      });
      const created = (res as any)?.data || res;
      setShowCreateSite(false);
      setNewSiteTitle('');
      setNewSiteSlug('');
      setNewSiteTemplate('landing');
      if (created?.id) {
        // Store the siteTemplate in section_content for the editor
        const pageRecord = {
          ...created,
          section_content: {
            ...(created.section_content || { blocks: [], settings: {} }),
            settings: {
              ...(created.section_content?.settings || {}),
              siteTemplate: newSiteTemplate,
            },
          },
        };
        setEditingPage(pageRecord);
        setView('site-editor');
      }
      loadPages();
    } catch (err: any) {
      setError(err?.message || 'Failed to create site page');
    }
  };

  // ── Open Builder ──
  const openBuilder = async (page: PageRecord) => {
    setEditingTemplate(null);
    setEditingPage(page);
    const config = page.section_content || { blocks: [], settings: {} };
    let loadedBlocks = config.blocks || [];

    // Pre-populate hero blocks with the entity's existing image if they have no imageUrl set
    const heroNeedsImage = loadedBlocks.some(b => b.blockKey === 'hero' && !b.props?.imageUrl);
    if (page.entity_id && heroNeedsImage) {
      try {
        let entityImageUrl = '';
        if (page.entity_type === 'offering') {
          const res: any = await api.get(`/admin/deals/${page.entity_id}`);
          const deal = res?.data || res;
          entityImageUrl = deal?.branding_json?.imageUrl || deal?.branding?.imageUrl || '';
        } else if (page.entity_type === 'property') {
          const res: any = await api.get(`/admin/properties/${page.entity_id}`);
          const prop = res?.data || res;
          entityImageUrl = prop?.images?.[0] || prop?.imageUrl || '';
        }
        if (entityImageUrl) {
          loadedBlocks = loadedBlocks.map(b =>
            b.blockKey === 'hero' && !b.props?.imageUrl
              ? { ...b, props: { ...b.props, imageUrl: entityImageUrl } }
              : b
          );
        }
      } catch { /* non-fatal — just open without pre-fill */ }
    }

    setBlocks(loadedBlocks);
    setPageSettings(config.settings || {});
    setCustomBlocks((config.settings as any)?.customBlocks || []);
    setView('builder');
  };

  // ── Open Template Builder ──
  const openTemplateBuilder = (tpl: TemplateRecord) => {
    setEditingTemplate(tpl);
    setEditingPage(null);
    const config = tpl.component_config || { blocks: [], settings: {} };
    setBlocks(config.blocks || []);
    setPageSettings(config.settings || {});
    setCustomBlocks((config.settings as any)?.customBlocks || []);
    setView('builder');
  };

  // ── Delete Page ──
  const deletePage = async (id: string) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await api.delete(`/admin/page-templates/components/instances/${id}`);
      loadPages();
    } catch { /* ignore */ }
  };

  // ── Builder: Block Operations ──
  const addBlock = (entry: BlockRegistryEntry) => {
    const newBlock: BlockInstance = {
      id: genBlockId(),
      blockKey: entry.key,
      props: { ...entry.defaultProps },
      visible: true,
      order: blocks.length,
    };
    setBlocks(prev => [...prev, newBlock]);
    setShowBlockPicker(false);
  };

  const updateBlockProps = (blockId: string, props: Record<string, any>) => {
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, props } : b));
  };

  const removeBlock = (blockId: string) => {
    setBlocks(prev => prev.filter(b => b.id !== blockId));
  };

  const toggleBlock = (blockId: string) => {
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, visible: !b.visible } : b));
  };

  const moveBlock = (blockId: string, direction: 'up' | 'down') => {
    setBlocks(prev => {
      const idx = prev.findIndex(b => b.id === blockId);
      if (idx < 0) return prev;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((b, i) => ({ ...b, order: i }));
    });
  };

  // ── Custom Block Creation ──
  const handleCustomBlockCreated = (entry: BlockRegistryEntry) => {
    setCustomBlocks(prev => [...prev, entry]);
    addBlock(entry);
  };

  // ── Resolve block entry (registry or custom) ──
  const resolveBlockEntry = (blockKey: string): BlockRegistryEntry | null => {
    return getBlockEntry(blockKey) || customBlocks.find(c => c.key === blockKey) || null;
  };

  // ── Save ──
  const handleSave = async () => {
    setSaving(true);
    try {
      const config: PageConfig = {
        blocks: blocks.map((b, i) => ({ ...b, order: i })),
        settings: { ...pageSettings, customBlocks: customBlocks as any },
      };
      if (editingTemplate) {
        // Saving a template — update component_config
        await api.put(`/admin/page-templates/components/${editingTemplate.id}`, {
          blocks: config.blocks,
          settings: config.settings,
        });
        setEditingTemplate(prev => prev ? { ...prev, component_config: config } : prev);
        setTemplates(prev => prev.map(t => t.id === editingTemplate.id ? { ...t, component_config: config } : t));
      } else if (editingPage) {
        await api.put(`/admin/page-templates/components/instances/${editingPage.id}`, {
          blocks: config.blocks,
          settings: config.settings,
        });
        setEditingPage(prev => prev ? { ...prev, section_content: config } : prev);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Publish ──
  const handlePublish = async () => {
    if (!editingPage) return;
    setSaving(true);
    try {
      const config: PageConfig = {
        blocks: blocks.map((b, i) => ({ ...b, order: i })),
        settings: { ...pageSettings, customBlocks: customBlocks as any },
      };
      await api.put(`/admin/page-templates/components/instances/${editingPage.id}`, {
        blocks: config.blocks,
        settings: config.settings,
        status: 'published',
      });
      setEditingPage(prev => prev ? { ...prev, status: 'published', section_content: config } : prev);
    } catch (err: any) {
      setError(err?.message || 'Failed to publish');
    } finally {
      setSaving(false);
    }
  };

  // ── Duplicate ──
  const duplicatePage = async (page: PageRecord) => {
    try {
      const templateId = await getDefaultTemplateId(page.entity_type || 'offering');
      if (!templateId) return;
      const res = await api.post('/admin/page-templates/components/instances', {
        templateId,
        entityType: page.entity_type,
        entityId: page.entity_id || null,
        title: `${page.title} (copy)`,
      });
      const created = (res as any)?.data || res;
      if (created?.id) {
        await api.put(`/admin/page-templates/components/instances/${created.id}`, {
          blocks: page.section_content?.blocks || [],
          settings: page.section_content?.settings || {},
        });
      }
      loadPages();
    } catch { /* ignore */ }
  };

  // ── Preview Config ──
  const previewConfig: PageConfig = {
    blocks: blocks.map((b, i) => ({ ...b, order: i })),
    settings: pageSettings,
  };

  const entityContext: EntityContext | undefined = editingPage?.entity_id
    ? {
        entityType: (editingPage.entity_type || 'offering') as 'offering' | 'property' | 'asset',
        entityId: editingPage.entity_id,
      }
    : undefined;

  const allCategories = [...getCategories(), ...(customBlocks.length > 0 ? ['custom'] : [])];

  const getBlocksForCategory = (cat: string): BlockRegistryEntry[] => {
    if (cat === 'custom') return customBlocks;
    return getBlocksByCategory(cat);
  };

  // Separate site pages from entity pages
  const entityPages = pages.filter((p) => p.entity_type !== 'site');
  const sitePages = pages.filter((p) => p.entity_type === 'site');

  const filteredPages = entityFilter === 'all'
    ? entityPages
    : entityPages.filter((p) => p.entity_type === entityFilter);

  const publishedPages = filteredPages.filter((p) => p.status === 'published');

  const groupedPublished: Record<'offering' | 'property' | 'asset', PageRecord[]> = {
    offering: publishedPages.filter((p) => p.entity_type === 'offering'),
    property: publishedPages.filter((p) => p.entity_type === 'property'),
    asset: publishedPages.filter((p) => p.entity_type === 'asset'),
  };

  /* ================================================================ */
  /*  Site Editor View (CMS-style section editor)                      */
  /* ================================================================ */
  if (view === 'site-editor' && editingPage) {
    return (
      <SitePageEditor
        page={{
          id: editingPage.id,
          title: editingPage.title,
          slug: editingPage.slug || '',
          status: editingPage.status,
          section_content: editingPage.section_content || { blocks: [], settings: {} },
          nav_visible: editingPage.nav_visible || false,
          nav_order: editingPage.nav_order || 0,
          section_visibility: editingPage.section_visibility || {},
        }}
        onBack={() => { setView('list'); loadPages(); }}
        onSaved={loadPages}
      />
    );
  }

  /* ================================================================ */
  /*  Builder View                                                     */
  /* ================================================================ */
  if (view === 'builder') {
    return (
      <div className="h-full flex flex-col min-w-0">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 px-3 sm:px-5 py-3 border-b border-border bg-background">
          <Button variant="ghost" size="sm" onClick={() => {
            if (cameFromExternal) { navigate(-1); } else { setEditingTemplate(null); setView('list'); loadPages(); }
          }}>
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold truncate">
              {editingTemplate ? editingTemplate.name : (editingPage?.title || 'Page Builder')}
            </h2>
            <p className="text-[11px] text-muted-foreground">
              {editingTemplate ? 'Template' : ''}
              {blocks.length} section{blocks.length !== 1 ? 's' : ''}
              {!editingTemplate && editingPage?.entity_type && (
                <span className="ml-1">• {editingPage.entity_type}</span>
              )}
              {!editingTemplate && editingPage?.status === 'published' && (
                <Badge variant="default" className="ml-2 text-[10px] py-0">Live</Badge>
              )}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowPageSettings(true)}>
            <Settings size={14} className="mr-1" /> Settings
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowPreview(true)}>
            <Eye size={14} className="mr-1" /> Preview
          </Button>
          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving}>
            <Save size={14} className="mr-1" /> {saving ? 'Saving...' : (editingTemplate ? 'Save Template' : 'Save')}
          </Button>
          {!editingTemplate && (
            <Button size="sm" onClick={handlePublish} disabled={saving}>
              <ExternalLink size={14} className="mr-1" /> Publish
            </Button>
          )}
        </div>

        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-w-0">
          {/* Left Panel: Section List */}
          <div className="w-full lg:w-[440px] lg:border-r border-border overflow-y-auto min-w-0 max-h-[55vh] lg:max-h-none">
            <div className="sticky top-0 z-10 bg-background border-b border-border px-3 sm:px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Page Sections
              </h3>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" onClick={() => setShowCustomCreator(true)} className="h-7 text-xs">
                  <Puzzle size={12} className="mr-1" /> Custom
                </Button>
                <Button variant="default" size="sm" onClick={() => setShowBlockPicker(true)} className="h-7 text-xs">
                  <Plus size={12} className="mr-1" /> Add Section
                </Button>
              </div>
            </div>

            <div className="p-4 space-y-2">
              {blocks.length === 0 && (
                <div className="text-center py-16">
                  <Layers size={36} className="mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground font-medium">No sections yet</p>
                  <p className="text-xs text-muted-foreground mt-1 max-w-[240px] mx-auto">
                    Click "Add Section" to start building. Each section becomes a configurable part of your page.
                  </p>
                </div>
              )}

              {blocks.map((block, idx) => (
                <SectionCard
                  key={block.id}
                  block={block}
                  entry={resolveBlockEntry(block.blockKey)}
                  onUpdate={(props) => updateBlockProps(block.id, props)}
                  onRemove={() => removeBlock(block.id)}
                  onToggle={() => toggleBlock(block.id)}
                  onMoveUp={() => moveBlock(block.id, 'up')}
                  onMoveDown={() => moveBlock(block.id, 'down')}
                  isFirst={idx === 0}
                  isLast={idx === blocks.length - 1}
                />
              ))}

              {blocks.length > 0 && (
                <button
                  onClick={() => setShowBlockPicker(true)}
                  className="w-full py-3 border-2 border-dashed border-muted-foreground/20 rounded-lg text-xs text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors flex items-center justify-center gap-1.5"
                >
                  <Plus size={14} /> Add another section
                </button>
              )}
            </div>
          </div>

          {/* Right Panel: Live Preview */}
          <div className="flex-1 overflow-y-auto bg-muted/30 p-3 sm:p-6 min-w-0">
            <div className="max-w-5xl mx-auto bg-background rounded-xl shadow-sm border border-border min-h-[60vh]">
              {blocks.length === 0 ? (
                <div className="flex items-center justify-center h-[60vh] text-muted-foreground">
                  <div className="text-center">
                    <Eye size={32} className="mx-auto mb-3 opacity-20" />
                    <p className="text-sm">Live preview will appear here</p>
                  </div>
                </div>
              ) : (
                <Suspense fallback={<div className="p-8 text-center text-muted-foreground text-sm">Loading preview...</div>}>
                  <PageRenderer config={previewConfig} entity={entityContext} isEditing />
                </Suspense>
              )}
            </div>
          </div>
        </div>

        {/* Block Picker Dialog */}
        <Dialog open={showBlockPicker} onOpenChange={setShowBlockPicker}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Section</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground -mt-2 mb-4">
              Choose a section type. Each becomes a configurable block on your page.
            </p>
            <div className="space-y-6">
              {allCategories.map(cat => {
                const catBlocks = getBlocksForCategory(cat);
                if (catBlocks.length === 0) return null;
                return (
                  <div key={cat}>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                      {CATEGORY_ICONS[cat]} {CATEGORY_LABELS[cat] || cat}
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {catBlocks.map(entry => {
                        const maxReached = entry.maxPerPage !== undefined &&
                          blocks.filter(b => b.blockKey === entry.key).length >= entry.maxPerPage;
                        return (
                          <button
                            key={entry.key}
                            onClick={() => !maxReached && addBlock(entry)}
                            disabled={maxReached}
                            className={`text-left p-3 rounded-lg border transition-colors ${
                              maxReached
                                ? 'border-border opacity-50 cursor-not-allowed'
                                : 'border-border hover:border-primary hover:bg-primary/5 cursor-pointer'
                            }`}
                          >
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <p className="text-sm font-medium">{entry.label}</p>
                              {maxReached && (
                                <span className="text-[9px] font-medium bg-muted text-muted-foreground rounded px-1 py-0.5">
                                  Max
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                              {entry.description} • {entry.schema.length} field{entry.schema.length !== 1 ? 's' : ''}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="pt-2 border-t border-border">
                <button
                  onClick={() => { setShowBlockPicker(false); setShowCustomCreator(true); }}
                  className="w-full text-left p-3 rounded-lg border border-dashed border-muted-foreground/30 hover:border-primary hover:bg-primary/5 transition-colors flex items-center gap-3"
                >
                  <Puzzle size={18} className="text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Create Custom Block</p>
                    <p className="text-[11px] text-muted-foreground">Define your own block with custom fields</p>
                  </div>
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Custom Block Creator */}
        <CustomBlockCreator
          open={showCustomCreator}
          onClose={() => setShowCustomCreator(false)}
          onCreated={handleCustomBlockCreated}
        />

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Page Preview</DialogTitle>
            </DialogHeader>
            <div className="min-h-[60vh]">
              <Suspense fallback={<div className="p-8 text-center">Loading...</div>}>
                <PageRenderer config={previewConfig} entity={entityContext} />
              </Suspense>
            </div>
          </DialogContent>
        </Dialog>

        {/* Page Settings Dialog */}
        <Dialog open={showPageSettings} onOpenChange={setShowPageSettings}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Page Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Page Title</label>
                <Input
                  value={pageSettings.title || ''}
                  onChange={(e) => setPageSettings(p => ({ ...p, title: e.target.value }))}
                  placeholder="Page title"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Description</label>
                <Textarea
                  value={pageSettings.description || ''}
                  onChange={(e) => setPageSettings(p => ({ ...p, description: e.target.value }))}
                  placeholder="Page description"
                  rows={2}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Max Width</label>
                <Select
                  value={pageSettings.maxWidth || 'xl'}
                  onValueChange={(v) => setPageSettings(p => ({ ...p, maxWidth: v as any }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sm">Small (768px)</SelectItem>
                    <SelectItem value="md">Medium (1024px)</SelectItem>
                    <SelectItem value="lg">Large (1152px)</SelectItem>
                    <SelectItem value="xl">Extra Large (1280px)</SelectItem>
                    <SelectItem value="full">Full Width</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Block Spacing</label>
                <Select
                  value={pageSettings.spacing || 'normal'}
                  onValueChange={(v) => setPageSettings(p => ({ ...p, spacing: v as any }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compact">Compact</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="relaxed">Relaxed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowPageSettings(false)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Error Toast */}
        {error && (
          <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 text-red-800 px-4 py-2 rounded-lg text-sm shadow-lg z-50 flex items-center gap-2">
            {error}
            <button onClick={() => setError('')} className="font-bold hover:text-red-900">&times;</button>
          </div>
        )}
      </div>
    );
  }

  /* ================================================================ */
  /*  List View                                                        */
  /* ================================================================ */
  return (
      <div className="max-w-6xl mx-auto space-y-5 sm:space-y-8 min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Page Builder</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Build pages from React components. Attach to offerings, properties, or assets.
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          {listTab === 'site' ? (
            <Button onClick={() => setShowCreateSite(true)} className="w-full sm:w-auto">
              <Globe size={16} className="mr-1.5" /> New Site Page
            </Button>
          ) : (
            <Button onClick={() => setShowCreate(true)} className="w-full sm:w-auto">
              <Plus size={16} className="mr-1.5" /> New Page
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          {error}
          <button onClick={() => setError('')} className="font-bold">&times;</button>
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center lg:justify-between gap-3">
        <div className="inline-flex w-full lg:w-auto overflow-x-auto rounded-lg border border-border p-1 bg-muted/20">
          {([
            { key: 'pages', label: 'Pages' },
            { key: 'published', label: 'Published Pages' },
            { key: 'site', label: 'Site Pages' },
            { key: 'templates', label: 'Templates' },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setListTab(tab.key)}
              className={`shrink-0 px-3 py-1.5 text-sm rounded-md transition-colors ${listTab === tab.key ? 'bg-background shadow-sm font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {listTab !== 'templates' && listTab !== 'site' && (
          <div className="inline-flex w-full lg:w-auto overflow-x-auto rounded-lg border border-border p-1 bg-muted/20">
            {([
              { key: 'all', label: 'All' },
              { key: 'offering', label: 'Offerings' },
              { key: 'property', label: 'Properties' },
              { key: 'asset', label: 'Assets' },
            ] as const).map((opt) => (
              <button
                key={opt.key}
                onClick={() => setEntityFilter(opt.key)}
                className={`shrink-0 px-3 py-1.5 text-sm rounded-md transition-colors ${entityFilter === opt.key ? 'bg-background shadow-sm font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
          <p className="text-sm">Loading pages...</p>
        </div>
      ) : listTab === 'templates' ? (
        templates.length === 0 ? (
          <Card className="p-10 text-center">
            <Layers size={36} className="mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No component templates found.</p>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {templates.map((tpl) => {
              const usage = pages.filter((p) => p.template_id === tpl.id).length;
              const blockCount = (tpl.component_config?.blocks || []).length;
              return (
                <Card key={tpl.id} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-semibold">{tpl.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{tpl.description || 'Component template'}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] py-0">{tpl.category || 'general'}</Badge>
                  </div>
                  <div className="mt-3 text-xs text-muted-foreground space-y-1">
                    <p>{blockCount} block{blockCount !== 1 ? 's' : ''} in template</p>
                    <p>Used by {usage} page{usage !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="mt-3">
                    <Button size="sm" variant="outline" onClick={() => openTemplateBuilder(tpl)} className="w-full">
                      <Edit3 size={13} className="mr-1" /> Edit Template
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )
      ) : listTab === 'site' ? (
        sitePages.length === 0 ? (
          <Card className="p-12 text-center">
            <Globe size={40} className="mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="text-lg font-semibold mb-1">No site pages yet</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              Create landing pages, about pages, and marketing funnels that render on your public site.
              Use marketing components like navbars, CTAs, pricing tables, and contact forms.
            </p>
            <Button onClick={() => setShowCreateSite(true)}>
              <Globe size={16} className="mr-1.5" /> Create Your First Site Page
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sitePages.map((page) => {
              const blockCount = page.section_content?.blocks?.length || 0;
              return (
                <Card key={page.id} className="overflow-hidden hover:shadow-md transition-shadow group">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-base truncate">{page.title}</CardTitle>
                        <div className="flex gap-1.5 mt-1.5 flex-wrap">
                          <Badge
                            variant={page.status === 'published' ? 'default' : 'secondary'}
                            className="text-[10px] py-0"
                          >
                            {page.status === 'published' ? 'Live' : 'Draft'}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] py-0">/{page.slug}</Badge>
                          {page.nav_visible && <Badge variant="outline" className="text-[10px] py-0 border-primary text-primary">Nav</Badge>}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pb-4 space-y-2">
                    <p className="text-xs text-muted-foreground">
                      {blockCount} section{blockCount !== 1 ? 's' : ''} &bull; Updated {fmtDate(page.updated_at)}
                    </p>
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" onClick={() => { setEditingPage(page); setView('site-editor'); }} className="flex-1">
                        <Edit3 size={14} className="mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => duplicatePage(page)}>
                        <Copy size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deletePage(page.id)}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      ) : listTab === 'published' ? (
        publishedPages.length === 0 ? (
          <Card className="p-10 text-center">
            <Layers size={36} className="mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No published pages for this filter.</p>
          </Card>
        ) : (
          <div className="space-y-5">
            {(['offering', 'property', 'asset'] as const).map((entityType) => {
              const items = groupedPublished[entityType];
              if (items.length === 0) return null;
              return (
                <div key={entityType} className="space-y-3">
                  <h3 className="text-sm font-semibold capitalize">{entityType} Pages</h3>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {items.map((page) => {
                      const blockCount = page.section_content?.blocks?.length || 0;
                      return (
                        <Card key={page.id} className="p-4">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold truncate">{page.title}</p>
                            <Badge variant="default" className="text-[10px] py-0">Live</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            {blockCount} section{blockCount !== 1 ? 's' : ''} • Updated {fmtDate(page.updated_at)}
                          </p>
                          <div className="mt-3">
                            <Button size="sm" onClick={() => openBuilder(page)} className="w-full">
                              <Edit3 size={14} className="mr-1" /> Edit Page
                            </Button>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : filteredPages.length === 0 ? (
        <Card className="p-12 text-center">
          <Layers size={40} className="mx-auto mb-4 text-muted-foreground/30" />
          <h3 className="text-lg font-semibold mb-1">No pages yet</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
            Create your first page to start building. Pages are composed of configurable
            sections — hero banners, content blocks, investment CTAs, galleries, and more.
          </p>
          <Button onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1.5" /> Create Your First Page
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredPages.map(page => {
            const deal = deals.find(d => d.id === page.entity_id);
            const blockCount = page.section_content?.blocks?.length || 0;
            return (
              <Card key={page.id} className="overflow-hidden hover:shadow-md transition-shadow group">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base truncate">{page.title}</CardTitle>
                      <div className="flex gap-1.5 mt-1.5">
                        <Badge
                          variant={page.status === 'published' ? 'default' : 'secondary'}
                          className="text-[10px] py-0"
                        >
                          {page.status === 'published' ? 'Live' : 'Draft'}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] py-0">{page.entity_type}</Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pb-4 space-y-2">
                  {deal && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Link2 size={11} /> {deal.title}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {blockCount} section{blockCount !== 1 ? 's' : ''} • Updated {fmtDate(page.updated_at)}
                  </p>
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" onClick={() => openBuilder(page)} className="flex-1">
                      <Edit3 size={14} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => duplicatePage(page)}>
                      <Copy size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deletePage(page.id)}>
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Page Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Page</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <label className="text-xs font-medium">Page Title</label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. OpenClaw Fund Page"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Entity Type</label>
              <Select value={newEntityType} onValueChange={setNewEntityType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="offering">Offering</SelectItem>
                  <SelectItem value="property">Property</SelectItem>
                  <SelectItem value="asset">Asset</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">
                Link to {newEntityType === 'offering' ? 'Offering' : newEntityType === 'property' ? 'Property' : 'Asset'} (optional)
              </label>
              <Select
                value={newEntityId || NONE_SENTINEL}
                onValueChange={(v) => {
                  const id = v === NONE_SENTINEL ? '' : v;
                  setNewEntityId(id);
                  if (newEntityType === 'offering') {
                    const deal = deals.find(d => d.id === id);
                    if (deal && !newTitle) setNewTitle(deal.title);
                  } else if (newEntityType === 'property') {
                    const prop = properties.find(p => p.id === id);
                    if (prop && !newTitle) setNewTitle(prop.title);
                  }
                }}
              >
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_SENTINEL}>None</SelectItem>
                  {newEntityType === 'offering' && deals.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
                  ))}
                  {newEntityType === 'property' && properties.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreatePage} disabled={!newTitle.trim()}>
              Create & Build
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Site Page Dialog */}
      <Dialog open={showCreateSite} onOpenChange={setShowCreateSite}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Site Page</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <label className="text-xs font-medium">Page Title</label>
              <Input
                value={newSiteTitle}
                onChange={(e) => setNewSiteTitle(e.target.value)}
                placeholder="e.g. About Us, Pricing, Contact"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">URL Path</label>
              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground">/</span>
                <Input
                  value={newSiteSlug}
                  onChange={(e) => setNewSiteSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="about-us"
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Leave blank to auto-generate from the title. Use "home" for the homepage.
              </p>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Page Template</label>
              <Select value={newSiteTemplate} onValueChange={(v) => {
                setNewSiteTemplate(v);
                const tpl = SITE_TEMPLATES.find(t => t.id === v);
                if (tpl) {
                  if (!newSiteTitle) setNewSiteTitle(tpl.label);
                  if (!newSiteSlug && tpl.id !== 'generic') setNewSiteSlug(tpl.id === 'landing' ? 'home' : tpl.id);
                }
              }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SITE_TEMPLATES.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Choose a styled template. All content is fully editable.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateSite(false)}>Cancel</Button>
            <Button onClick={handleCreateSitePage} disabled={!newSiteTitle.trim()}>
              Create & Build
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PageBuilder;
